import { Equipment } from "@domain/library/equipment";
import {
	ExerciseBodyPart,
	ExerciseCategory,
	ExerciseDifficulty,
	ExerciseStartingPosition,
	isExerciseBodyPart,
	isExerciseCategory,
	isExerciseDifficulty,
	isExerciseStartingPosition,
} from "@domain/exercise/exercise";
import { WordingKey } from "@typings/i18n-typings";
import { I18nFormat } from "@core/i18n/i18n";

const exerciseCategoryWordings = {
	[ExerciseCategory.WarmUp]: "exercise.category.value.warmUp",
	[ExerciseCategory.Stretching]: "exercise.category.value.stretching",
	[ExerciseCategory.Strengthening]: "exercise.category.value.strengthening",
	[ExerciseCategory.CoordinationAndBalance]: "exercise.category.value.coordinationBalance",
} satisfies Record<ExerciseCategory, WordingKey>;

const exerciseBodyPartWordings = {
	[ExerciseBodyPart.UpperLimbs]: "exercise.bodyPart.value.upperLimbs",
	[ExerciseBodyPart.LowerLimbs]: "exercise.bodyPart.value.lowerLimbs",
	[ExerciseBodyPart.HeadTrunk]: "exercise.bodyPart.value.headAndTrunk",
	[ExerciseBodyPart.FullBody]: "exercise.bodyPart.value.fullBody",
} satisfies Record<ExerciseBodyPart, WordingKey>;

const exerciseStartingPositionWordings = {
	[ExerciseStartingPosition.LyingDown]: "exercise.startingPosition.value.lyingDown",
	[ExerciseStartingPosition.Kneeling]: "exercise.startingPosition.value.kneeling",
	[ExerciseStartingPosition.SittingChair]: "exercise.startingPosition.value.sitting.chair",
	[ExerciseStartingPosition.SittingFloor]: "exercise.startingPosition.value.sitting.floor",
	[ExerciseStartingPosition.Standing]: "exercise.startingPosition.value.standing",
} satisfies Record<ExerciseStartingPosition, WordingKey>;

const exerciseDifficultyWordings = {
	[ExerciseDifficulty.Easy]: "exercise.difficulty.value.easy",
	[ExerciseDifficulty.Moderate]: "exercise.difficulty.value.moderate",
	[ExerciseDifficulty.Hard]: "exercise.difficulty.value.hard",
} satisfies Record<ExerciseDifficulty, WordingKey>;

const exerciseWithWheelchairWordings = {
	true: "exercise.withWheelchair.value.yes",
	false: "exercise.withWheelchair.value.no",
} satisfies Record<"true" | "false", WordingKey>;

// HelpNeeded = CaregiverAssistance
const exerciseHelpNeededWordings = {
	true: "exercise.helpNeeded.value.yes",
	false: "exercise.helpNeeded.value.no",
} satisfies Record<"true" | "false", WordingKey>;

const exerciseCaregiverAssistanceWordings = {
	true: "exercise.caregiverAssistance.value.yes",
	false: "exercise.caregiverAssistance.value.no",
} satisfies Record<"true" | "false", WordingKey>;

const LABEL_KEY_EMPTY_SECTION = "exerciseDetailsModal.emptySection";

export class ExerciseFormatter {

	constructor(private readonly format: I18nFormat) {
		this.formatExerciseBodyPart = this.formatExerciseBodyPart.bind(this);
		this.formatExerciseBodyParts = this.formatExerciseBodyParts.bind(this);
		this.formatExerciseCategory = this.formatExerciseCategory.bind(this);
		this.formatExerciseStartingPosition = this.formatExerciseStartingPosition.bind(this);
		this.formatEquipements = this.formatEquipements.bind(this);
		this.formatExerciseDifficulty = this.formatExerciseDifficulty.bind(this);
		this.formatExerciseWithWheelchair = this.formatExerciseWithWheelchair.bind(this);
		this.formatExerciseHelpNeeded = this.formatExerciseHelpNeeded.bind(this);
		this.formatExerciseCaregiverAssistance = this.formatExerciseCaregiverAssistance.bind(this);
	}

	formatExerciseBodyPart(bodyPart: string): string {
		return (isExerciseBodyPart(bodyPart) ? this.format(exerciseBodyPartWordings[bodyPart]) : bodyPart);
	}

	formatExerciseBodyParts(bodyParts: string[]): string {
		return bodyParts.length > 0
				? bodyParts.map(bodyPart => this.formatExerciseBodyPart(bodyPart)).join(", ")
				: this.format(LABEL_KEY_EMPTY_SECTION);
	}

	formatExerciseCategory(category: string): string {
		return (isExerciseCategory(category) ? this.format(exerciseCategoryWordings[category]) : category);
	}

	formatExerciseStartingPosition(startingPosition: string): string {
		return isExerciseStartingPosition(startingPosition)
				? this.format(exerciseStartingPositionWordings[startingPosition])
				: startingPosition;
	}

	formatEquipements(equipments: Equipment[]): string {
		return equipments.length > 0 ? equipments.map(e => e.name).join(", ") : this.format("exerciseDetailsModal.emptySection");
	}

	formatExerciseDifficulty(difficulty: string): string {
		return isExerciseDifficulty(difficulty) ? this.format(exerciseDifficultyWordings[difficulty]) : difficulty;
	}

	formatExerciseWithWheelchair(withWheelchair: boolean): string {
		return this.format(exerciseWithWheelchairWordings[withWheelchair ? "true" : "false"]);
	}
	
	formatExerciseHelpNeeded(helpNeeded: boolean): string {
		return this.format(exerciseHelpNeededWordings[helpNeeded ? "true" : "false"]);
	}
			
	formatExerciseCaregiverAssistance(caregiverAssistance: boolean): string {
		return this.format(exerciseCaregiverAssistanceWordings[caregiverAssistance ? "true" : "false"]);
	}
}
